import { useState, useEffect, useCallback, useMemo } from 'react';
import { liveMessageDataHub } from '../data/liveMessageData/liveMessageDataHub';
import { LiveMessage, LiveImageStatus, constructLiveMessage } from '../data/liveMessageData/liveMessage';
import { Character } from '@/app/lib/types/character';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import { getGlobalStickyScrollCallback } from './useStickyScrollCallback';

/**
 * Hook to get current messages from the singleton
 * Automatically subscribes to updates and re-renders when data changes
 */
export function useLiveMessages(): LiveMessage[] {
  const [messages, setMessages] = useState<LiveMessage[]>([]);

  useEffect(() => {
    // Subscribe to message updates
    const unsubscribe = liveMessageDataHub.subscribe(setMessages);
    
    // Get initial messages
    setMessages(liveMessageDataHub.getMessages());
    
    return unsubscribe;
  }, []);

  return messages;
}

/**
 * Hook that provides message management functions
 * These functions work with the singleton directly
 */
export function useLiveMessageActions(onMessageAdded?: () => void) {
  const { userEmail } = useAuth();

  const setContext = useCallback((avatarId: string) => {
    if (userEmail) {
      liveMessageDataHub.setContext(userEmail, avatarId);
    }
  }, [userEmail]);

  const addMessage = useCallback(async (message: {
    id: string;
    content: string;
    role: 'user' | 'assistant';
    sender_id: string;
    sender_name: string;
    created_at?: string;
    imageUrl?: string;
    isStreaming?: boolean;
  }) => {
    const liveMessage = constructLiveMessage(message);
    await liveMessageDataHub.addMessage(liveMessage);
    onMessageAdded?.();
    const callback = getGlobalStickyScrollCallback();
    if (callback) {
      // console.log('🔄 Triggering sticky scroll from addMessage');
      callback();
    } else {
      console.log('⚠️ No global sticky scroll callback registered');
    }
  }, [onMessageAdded]);

  const addMessageFrontendOnly = useCallback((message: {
    id: string;
    content: string;
    role: 'user' | 'assistant';
    sender_id: string;
    sender_name: string;
    created_at?: string;
    imageUrl?: string;
    isStreaming?: boolean;
  }) => {
    const liveMessage = constructLiveMessage(message);
    liveMessageDataHub.addMessageFrontendOnly(liveMessage);
    onMessageAdded?.();
    const callback = getGlobalStickyScrollCallback();
    if (callback) {
      // console.log('🔄 Triggering sticky scroll from addMessageFrontendOnly');
      callback();
    } else {
      console.log('⚠️ No global sticky scroll callback registered');
    }
  }, [onMessageAdded]);

  const addMessageDBOnly = useCallback(async (message: {
    id: string;
    content: string;
    role: 'user' | 'assistant';
    sender_id: string;
    sender_name: string;
    created_at?: string;
    imageUrl?: string;
    isStreaming?: boolean;
  }) => {
    const liveMessage = constructLiveMessage(message);
    await liveMessageDataHub.addMessageDBOnly(liveMessage);
    onMessageAdded?.();
    const callback = getGlobalStickyScrollCallback();
    if (callback) {
      // console.log('🔄 Triggering sticky scroll from addMessageDBOnly');
      callback();
    } else {
      console.log('⚠️ No global sticky scroll callback registered');
    }
  }, [onMessageAdded]);

  const updateMessage = useCallback(async (messageId: string, content?: string, imageUrl?: string, isStreaming?: boolean) => {
    await liveMessageDataHub.updateMessage(messageId, content, imageUrl, isStreaming);
  }, []);

  const appendMessage = useCallback((messageId: string, newContent: string) => {
    liveMessageDataHub.appendMessage(messageId, newContent);
    const callback = getGlobalStickyScrollCallback();
    if (callback) {
      // console.log('🔄 Triggering sticky scroll from appendMessage');
      callback();
    } else {
      console.log('⚠️ No global sticky scroll callback registered');
    }
  }, []);

  const removeMessage = useCallback(async (messageId: string) => {
    await liveMessageDataHub.removeMessage(messageId);
  }, []);

  const editMessage = useCallback(async (messageId: string, newContent: string) => {
    await liveMessageDataHub.updateMessage(messageId, newContent);
  }, []);

  const clearMessages = useCallback(() => {
    liveMessageDataHub.clearMessages();
  }, []);

  const findMessage = useCallback((messageId: string) => {
    return liveMessageDataHub.findMessage(messageId);
  }, []);

  const getLastMessageWithImage = useCallback(() => {
    return liveMessageDataHub.getLastMessageWithImage();
  }, []);

  // MEMOIZED return object to prevent infinite loops
  return useMemo(() => ({
    addMessage,
    updateMessage,
    appendMessage,
    removeMessage,
    editMessage,
    clearMessages,
    setContext,
    findMessage,
    getLastMessageWithImage,
    addMessageFrontendOnly,
    addMessageDBOnly
  }), [addMessage, updateMessage, appendMessage, removeMessage, editMessage, clearMessages, setContext, findMessage, getLastMessageWithImage, addMessageFrontendOnly, addMessageDBOnly]);
}

/**
 * Hook for image status updates
 * Provides a trigger that updates when any image status changes
 */
export function useImageStatusUpdates() {
  const [updateTrigger, setUpdateTrigger] = useState(0);

  useEffect(() => {
    const callbackId = `hook-${Date.now()}`;
    let unsubscribe: (() => void) | null = null;
    
    const handleImageStatusChange = (messageId: string, status: LiveImageStatus, imageData?: any) => {
      console.log(`Image status changed for message ${messageId}: ${status}`, imageData ? 'with imageData' : 'no imageData');
      // Force re-render when image status changes
      setUpdateTrigger(prev => prev + 1);
    };
    
    // Import the callback registration functions
    import('../data/liveMessageData/liveMessage').then(({ registerImageStatusCallback, unregisterImageStatusCallback }) => {
      registerImageStatusCallback(callbackId, handleImageStatusChange);
      
      // Store the unsubscribe function
      unsubscribe = () => {
        unregisterImageStatusCallback(callbackId);
      };
    });

    // Return cleanup function from useEffect
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  return updateTrigger;
}

/**
 * Hook that combines messages and image status updates
 * Useful when you need both message data and want to re-render on image changes
 */
export function useLiveMessagesWithImageUpdates() {
  const messages = useLiveMessages();
  const imageUpdateTrigger = useImageStatusUpdates();
  
  return { messages, imageUpdateTrigger };
}

/**
 * Hook to get specific message by ID
 */
export function useLiveMessage(messageId: string): LiveMessage | undefined {
  const messages = useLiveMessages();
  return messages.find(msg => msg.id === messageId);
}

/**
 * Hook to get message count
 */
export function useLiveMessageCount(): number {
  const messages = useLiveMessages();
  return messages.length;
}

/**
 * Hook to check if messages are empty
 */
export function useIsMessagesEmpty(): boolean {
  const messages = useLiveMessages();
  return messages.length === 0;
}

/**
 * Hook to get filtered messages
 */
export function useFilteredLiveMessages(filter: (message: LiveMessage) => boolean): LiveMessage[] {
  const messages = useLiveMessages();
  return messages.filter(filter);
}

/**
 * Hook to get user messages only
 */
export function useUserMessages(): LiveMessage[] {
  return useFilteredLiveMessages(msg => msg.role === 'user');
}

/**
 * Hook to get assistant messages only
 */
export function useAssistantMessages(): LiveMessage[] {
  return useFilteredLiveMessages(msg => msg.role === 'assistant');
}

/**
 * Hook to get the last message
 */
export function useLastMessage(): LiveMessage | undefined {
  const messages = useLiveMessages();
  return messages[messages.length - 1];
} 